<script setup lang="ts">
import { computed, ref, useSlots } from 'vue';
import { v4 } from 'uuid';
import SlateIcon from '@/slate_ui/SlateIcons/SlateIcon.vue';

export interface CheckboxProps {
  modelValue: boolean;
  label?: string;
  name?: string;
  disabled?: boolean;
  large?: boolean;
}

const props = withDefaults(defineProps<CheckboxProps>(), {
  'modelValue': false,
});

// I tried generating this id within withDefaults, but all checkboxes were
// getting the same id.  So Vue is doing something internally where it is sharing
// those default props.  At least when more than one checkbox is present on a page.
const id = ref(v4());

const slots = useSlots();

const labelStyle = computed(() => {
  let style = '';
  if (props.disabled) {
    style = `
      cursor: default;
      color: var(--primary-light-grey);
      pointer-events: none;
    `;
  } else {
    style = `
      cursor: pointer;
    `;
  }

  if (props.modelValue) {
    style += `
      color: var(--primary-blue);
    `;
  }

  return (
    style +
    `
    user-select: none;
  `
  );
});
</script>

<template>
  <input
    type="checkbox"
    :disabled="disabled"
    :id="name ?? id"
    :name="name"
    :checked="modelValue"
    @input="$emit('update:modelValue', ($event.target as HTMLInputElement)?.checked)" />
  <div class="checkbox-container">
    <div
      class="checkbox"
      :class="{ checked: modelValue, large: large }"
      @click="$emit('update:modelValue', !modelValue)">
      <SlateIcon
        icon="square-check"
        v-if="modelValue" />
      <SlateIcon
        icon="square"
        v-if="!modelValue" />
    </div>
    <label
      v-if="slots.default || label"
      :for="name ?? id"
      :style="labelStyle">
      <slot></slot>
      {{ label }}
    </label>
    <slot
      name="custom-label"
      :labelStyle="labelStyle"></slot>
  </div>
</template>

<style scoped>
input[type='checkbox'] {
  -moz-appearance: none;
  -webkit-appearance: none;
  -o-appearance: none;
  appearance: none;
  border: 0;
}

input:focus + .checkbox-container > .checkbox {
  background: radial-gradient(circle, rgba(var(--primary-400) / 0.5) 0%, rgba(var(--primary-400) / 0) 100%);
  border-radius: 2px;
}
/* 
A bit of hack to only show the focus border when the user is tabbing through inputs.
So when the user clicks on the checkbox the border doesn't show.
*/
input:hover + .checkbox-container > .checkbox {
  background: radial-gradient(circle, rgba(var(--primary-400) / 0.5) 0%, rgba(var(--primary-400) / 0) 100%);
  border-radius: 2px;
}

.checkbox-container {
  display: inline-flex;
  width: fit-content;
  align-items: center;

  & .checkbox {
    color: rgb(var(--gray-700));
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 5px;
    cursor: pointer;
    font-size: var(--size-3);

    &.large {
      font-size: var(--size-4);
    }
  }

  & .checkbox.checked {
    color: rgb(var(--primary-700));
  }

  & label {
    font-size: 12px;
    textwrap: none;
    margin-bottom: 0;
  }
}
</style>
